import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { getFxRates } from '~/services/fxService';

import { derivePropertySetup, resolveFxRate } from './propertySetupModel';
import type { PropertySetupValues } from './types';

// Local session state for the Property Setup tab - mirrors useInclusionDrafts: in-memory
// `useState`, no persistence, reset when the offer changes. The "Save Session" toolbar
// button stays disabled until svc-offer draft persistence is wired (XTRNT-1073).
export function usePropertySetup(offerId: string | undefined) {
  const [values, setValues] = useState<PropertySetupValues | null>(null);

  const { data: related } = useGetOpportunityRelated(offerId);
  const { data: opportunity } = useGetOpportunity<App.OpportunityPage>(offerId, { enabled: !!offerId });
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: getFxRates, staleTime: Infinity });

  // Prefill once per offerId when related data is available. Guarded by a ref so a
  // background refetch of `related`/`opportunity` never clobbers user edits (e.g. fxRate).
  const prefilledFor = useRef<string | undefined>(undefined);
  // Guards the FX auto-populate to at most once per prefill. Without this, clearing the
  // FX rate field (setFxRate(null)) would immediately re-populate it on the next render
  // — especially for AUD, which always resolves to 1.
  const fxAutoPopulated = useRef(false);

  useEffect(() => {
    setValues(null);
    prefilledFor.current = undefined;
    fxAutoPopulated.current = false;
  }, [offerId]);

  useEffect(() => {
    if (!related || prefilledFor.current === offerId) {
      return;
    }

    const roomAssets = [...(related.deals ?? []), ...(related.rooms ?? [])]
      .map((deal) => deal.room_asset)
      .filter((asset): asset is OpportunityRoomAsset => Boolean(asset));

    setValues(derivePropertySetup({ opportunity, deals: related.deals ?? [], roomAssets }));
    prefilledFor.current = offerId;
  }, [offerId, related, opportunity]);

  // offerCountry and subDestination are read-only and derived from the opportunity page,
  // which may load after `related`. Sync them whenever the opportunity arrives without
  // touching the user-editable fxRate/prvReqLeadPrice.
  useEffect(() => {
    if (!opportunity) {
      return;
    }

    const country = opportunity.location?.destination__c ?? null;
    const subDestination = opportunity.location?.sub_destination__c ?? null;
    setValues((current) => (current ? { ...current, offerCountry: country, subDestination } : current));
  }, [opportunity]);

  // Auto-populate fxRate once rates load and a currency is known. Runs at most once per
  // prefill (guarded by fxAutoPopulated ref) so the user can clear the field for manual
  // entry without it being silently re-populated.
  useEffect(() => {
    if (fxAutoPopulated.current || values?.fxRate !== null) {
      return;
    }

    if (!values.currency) {
      return;
    }

    const rate = resolveFxRate(fxRates, values.currency);
    if (rate === null) {
      return;
    }

    setValues({ ...values, fxRate: rate });
    fxAutoPopulated.current = true;
  }, [values, fxRates]);

  const setFxRate = useCallback((fxRate: number | null) => {
    setValues((current) => (current ? { ...current, fxRate } : current));
  }, []);

  const setPrvReqLeadPrice = useCallback((prvReqLeadPrice: number | null) => {
    setValues((current) => (current ? { ...current, prvReqLeadPrice } : current));
  }, []);

  // Re-runs resolveFxRate against the latest rates - used by the "Auto" toggle.
  const autoResolveFxRate = useCallback(() => {
    setValues((current) => {
      if (!current?.currency) {
        return current;
      }

      return { ...current, fxRate: resolveFxRate(fxRates, current.currency) };
    });
  }, [fxRates]);

  return { values, setFxRate, setPrvReqLeadPrice, autoResolveFxRate };
}
